/**
 * Map System — Shared Types & Constants
 *
 * Central type definitions for the world map system (builder editor + player widget).
 * All map-related components import from here to avoid duplication.
 */

// ─── World Map Object ───

/** Renderer types — extensible for future map generators */
export type MapRendererType = "media" | "blank" | "geo" | "tiles" | "procedural" | "hex";

/** User-facing labels and metadata for renderer types */
export const MAP_RENDERER_OPTIONS: { value: MapRendererType; label: string; icon: string; description: string }[] = [
  { value: "media", label: "Image", icon: "image", description: "Upload or generate a custom map image" },
  { value: "blank", label: "Blank Canvas", icon: "square", description: "Empty canvas — place nodes freely" },
  { value: "geo", label: "World Map", icon: "globe", description: "Real-world geography (Protomaps + MapLibre)" },
  // Extensible — add more renderer types here
];

/**
 * A first-class map object. Each map is stored in the `world_maps` table
 * and owns its own nodes, layers, connections, and renderer config.
 * The `renderer_type` field determines which renderer component is used
 * to display the map (currently only "media" is implemented).
 */
export interface WorldMap {
  id: string;
  world_id: string;
  parent_map_id: string | null;
  linked_entity_id: string | null;
  linked_node_id: string | null;
  name: string;
  renderer_type: MapRendererType;
  renderer_config: MapRendererConfig;
  media_url: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/**
 * Renderer-specific config stored in `world_maps.renderer_config`.
 * The "media" renderer uses layers, connections, and scale.
 * Future renderers (tiles, procedural, hex) can add their own keys.
 */
export interface MapRendererConfig {
  layers?: MapLayer[];
  connections?: MapConnection[];
  scale?: MapScaleConfig;
  /** Global display defaults for all nodes on this map/variant */
  display_defaults?: MapDisplayDefaults;
  /** Tile renderer config (future) */
  tile_config?: {
    tile_set: string;
    tile_size: number;
    grid_width: number;
    grid_height: number;
    tiles: number[][];
  };
  /** Procedural renderer config (future) */
  procedural_config?: {
    seed: number;
    algorithm: string;
    params: Record<string, any>;
  };
  /** Geo renderer config (Protomaps + MapLibre) */
  geo_config?: GeoRendererConfig;
  /** Any additional renderer-specific settings */
  [key: string]: any;
}

// ─── Geo Renderer Config ───

/**
 * Configuration for the "geo" renderer type (real-world maps via MapLibre GL JS + Protomaps).
 * Stored in `renderer_config.geo_config`.
 */
export interface GeoRendererConfig {
  /** Map center as [longitude, latitude] */
  center: [number, number];
  /** Initial zoom level (0-22) */
  zoom: number;
  /** Custom Protomaps PMTiles URL or MapLibre style URL */
  style_url?: string;
  /** Optional bounding box [[sw_lng, sw_lat], [ne_lng, ne_lat]] */
  bounds?: [[number, number], [number, number]];
  /** Whether to use dark theme for map tiles */
  dark_theme?: boolean;
}

// ─── Node Display Options ───

/**
 * Per-node visual display options stored in `world_map_nodes.display`.
 * Each field is optional — if omitted, the map's `display_defaults` are used.
 */
export interface MapNodeDisplay {
  show_text?: boolean;
  text_color?: string;
  show_icon?: boolean;
  icon_color?: string;
  icon_name?: string; // lucide icon name, e.g. "castle", "sword", "tree"
  show_outline?: boolean;
  outline_color?: string;
  show_subtitle?: boolean;
  subtitle?: string;
  subtitle_color?: string;
  /** Optional faction association for territory coloring */
  faction_id?: string;
  /** Polygon vertices for free-form area shapes (normalised 0-1 coords) */
  polygon_points?: { x: number; y: number }[];
  /** Area shape: "rect" (default) or "circle" */
  area_shape?: "rect" | "circle";
  /** Area fill color (overrides outline_color for fill) */
  area_fill_color?: string;
  /** Area fill opacity (0-1, overrides global default) */
  area_fill_opacity?: number;
  /** Whether to show the area border/fill at all */
  show_area?: boolean;
  /** Longitude for geo-positioned nodes */
  lng?: number;
  /** Latitude for geo-positioned nodes */
  lat?: number;
  /** Custom image URL for marker display */
  image_url?: string;
  /** Whether this node scales with map zoom (true) or stays fixed screen size (false). Overrides default. */
  scale_with_zoom?: boolean;
  /** Fixed size in meters when scale_with_zoom is false (geo maps) */
  fixed_size_meters?: number;
}

/**
 * Global display defaults for a map or variant, stored in renderer_config.display_defaults.
 * These apply to all nodes unless overridden per-node.
 */
export interface MapDisplayDefaults {
  show_text: boolean;
  text_color: string;
  show_icon: boolean;
  icon_color: string;
  default_icon: string;
  show_outline: boolean;
  outline_color: string;
  show_subtitle: boolean;
  subtitle_color: string;
  /** Point node size (px, before zoom) */
  point_size: number;
  /** Area border width (px) */
  area_border_width: number;
  /** Area fill opacity (0-1) */
  area_fill_opacity: number;
  /** Whether nodes scale with map zoom by default (true = scale, false = fixed screen size) */
  scale_with_zoom: boolean;
  /** Default fixed size in meters when scale_with_zoom is false (geo maps only) */
  fixed_size_meters: number;
}

export const DEFAULT_DISPLAY: MapDisplayDefaults = {
  show_text: true,
  text_color: "hsl(var(--foreground))",
  show_icon: true,
  icon_color: "hsl(var(--muted-foreground))",
  default_icon: "map-pin",
  show_outline: false,
  outline_color: "hsl(var(--primary) / 0.5)",
  show_subtitle: false,
  subtitle_color: "hsl(var(--muted-foreground) / 0.7)",
  point_size: 20,
  area_border_width: 2,
  area_fill_opacity: 0.05,
  scale_with_zoom: false,
  fixed_size_meters: 500,
};

// ─── Map Variant ───

/**
 * A variant is a specific visual representation of a map.
 * Each map can have multiple variants (e.g. "Satellite", "Political", "Hex Grid").
 * One variant per map is marked active at a time.
 */
export interface MapVariant {
  id: string;
  map_id: string;
  world_id: string;
  name: string;
  renderer_type: MapRendererType;
  renderer_config: MapRendererConfig;
  media_url: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// ─── Core Data Types ───

export interface MapNode {
  id: string;
  world_id: string;
  parent_node_id: string | null;
  entity_id: string | null;
  map_id: string | null;
  linked_map_id: string | null;
  /** When set, this node belongs to a specific map variant only. NULL = shared across all variants. */
  variant_id?: string | null;
  label: string;
  node_type: MapNodeType;
  x: number;
  y: number;
  width: number | null;
  height: number | null;
  map_image_url: string | null;
  sort_order: number;
  /** Per-node visual display overrides */
  display: MapNodeDisplay;
}

export type MapNodeType = "point" | "area";

export interface MapLayer {
  id: string;
  label: string;
  imageUrl: string;
  opacity: number;
  visible: boolean;
  /** Geo overlay bounds [[sw_lng, sw_lat], [ne_lng, ne_lat]] */
  bounds?: [[number, number], [number, number]];
  /** Minimum zoom level at which the layer starts fading in (default: 0) */
  minZoom?: number;
  /** Maximum zoom level at which the layer is fully visible (default: 22) */
  maxZoom?: number;
  /** Whether the layer scales with map zoom (true) or stays fixed size (false). Default true. */
  scalable?: boolean;
}

export interface MapBreadcrumb {
  id: string | null;
  label: string;
}

// ─── Connection / Road ───

export interface MapConnection {
  id: string;
  from: string;
  to: string;
  style: "road" | "river" | "trail" | "border" | "sea_route" | "shuttle" | "portal" | "custom";
  label?: string;
  /** Link to a real story entity (e.g. "Transport Shuttle", "Ancient Highway") */
  entity_id?: string;
  /** Short note for AI context */
  description?: string;
  /** Whether the connection is bidirectional (default true) */
  bidirectional?: boolean;
  /** Stored distance between the two connected locations (in map's scale unit) */
  distance?: number;
  /** Travel time in hours (pre-calculated, optional) */
  travel_time_hours?: number;
}

// ─── Config stored in world.config ───

export interface MapConfig {
  world_map_image_url?: string;
  world_map_layers?: MapLayer[];
  world_map_connections?: MapConnection[];
  /** Per-level scale config keyed by `map_scale_{nodeId|root}` */
  world_map_scale?: MapScaleConfig;
  /** Per-node layers keyed by `map_layers_{nodeId}` */
  [key: string]: any;
}

// ─── Grid ───

export type GridSize = 10 | 20 | 50;

export interface GridConfig {
  enabled: boolean;
  size: GridSize;
  snap: boolean;
}

// ─── Map Scale (per map level) ───

export interface MapScaleConfig {
  /** Total distance the map represents edge-to-edge */
  totalDistance: number;
  /** Distance unit label */
  unit: string;
  /** Speed modifier for travel time calc (distance per time-unit) */
  speedModifier: number;
}

export const DISTANCE_UNIT_PRESETS = [
  { label: "Feet", value: "ft", defaultScale: 100 },
  { label: "Meters", value: "m", defaultScale: 100 },
  { label: "Miles", value: "mi", defaultScale: 50 },
  { label: "Kilometers", value: "km", defaultScale: 80 },
  { label: "Leagues", value: "leagues", defaultScale: 100 },
  { label: "AU", value: "AU", defaultScale: 10 },
  { label: "Light Years", value: "ly", defaultScale: 5 },
  { label: "Parsecs", value: "pc", defaultScale: 3 },
  { label: "Hexes", value: "hexes", defaultScale: 20 },
  { label: "Days Travel", value: "days", defaultScale: 10 },
  { label: "Custom", value: "custom", defaultScale: 100 },
] as const;

export const DEFAULT_MAP_SCALE: MapScaleConfig = {
  totalDistance: 100,
  unit: "leagues",
  speedModifier: 1,
};

export const DEFAULT_GRID: GridConfig = {
  enabled: false,
  size: 20,
  snap: true,
};

// ─── Location Entity Constants ───

// IMPORTANT: LOCATION_ENTITY_TYPES must match LOCATION_TYPES in
// supabase/functions/_shared/world-tools/helpers.ts — if you add a type
// here, add it there too.
// The frontend set drives map node placement UI.
// The backend set drives hierarchy resolution + place_locations_on_map.
/** Entity types that are considered "location-like" for map placement */
export const LOCATION_ENTITY_TYPES = new Set([
  // Shared / generic
  "location", "place", "region", "city", "building", "district", "continent",
  "planet", "town", "village", "dungeon", "cave", "kingdom", "fortress", "temple",
  // Natural world (fantasy / outdoor)
  "forest", "mountain", "ocean", "island", "empire", "realm", "port",
  "castle", "settlement", "wasteland", "swamp", "desert", "tundra", "valley", "river", "lake",
  // Sci-fi / advanced settings
  "ship", "vehicle", "station", "deck", "room", "moon", "asteroid", "area", "sector",
  "zone", "habitat", "dock", "outpost", "country", "stargate",
]);

/** Relationship types that imply "child is spatially inside parent" */
export const CHILD_TO_PARENT_RELS = new Set([
  "contained_in", "inside", "part_of", "located_in", "within",
  "orbiting", "docked_at", "anchored_at", "stationed_at", "housed_in",
  "nested_in", "mounted_on", "attached_to", "embedded_in",
]);

/** Inverse: relationship types that imply "parent contains child" */
export const PARENT_TO_CHILD_RELS = new Set([
  "contains", "holds", "houses", "encompasses", "surrounds",
]);

// ─── Viewport / Interaction State ───

export interface ViewportState {
  zoom: number;
  pan: { x: number; y: number };
}

export const DEFAULT_VIEWPORT: ViewportState = {
  zoom: 1,
  pan: { x: 0, y: 0 },
};

export const ZOOM_LIMITS = { min: 0.25, max: 5 } as const;

// ─── Travel System ───

export interface TravelResult {
  distance: number;
  unit: string;
  travelTime: number;
  varianceApplied: number;
}

// ─── Connection Style Visuals ───

export const CONNECTION_STYLES: Record<MapConnection["style"], { color: string; dash?: string; width: number; label: string }> = {
  road:      { color: "hsl(var(--foreground) / 0.4)", width: 2, label: "Road" },
  trail:     { color: "hsl(var(--foreground) / 0.25)", dash: "4 4", width: 1.5, label: "Trail" },
  river:     { color: "hsl(210 80% 55% / 0.5)", width: 2.5, label: "River" },
  border:    { color: "hsl(var(--destructive) / 0.3)", dash: "6 3", width: 1.5, label: "Border" },
  sea_route: { color: "hsl(200 70% 50% / 0.35)", dash: "8 4", width: 2, label: "Sea Route" },
  shuttle:   { color: "hsl(270 70% 60% / 0.5)", dash: "3 6", width: 2, label: "Shuttle" },
  portal:    { color: "hsl(300 80% 60% / 0.5)", dash: "2 4 6 4", width: 2.5, label: "Portal" },
  custom:    { color: "hsl(var(--foreground) / 0.3)", dash: "5 5", width: 1.5, label: "Custom" },
};

// ─── Icon Registry for Map Nodes ───

/** Available icons for map node display. Mapped to lucide icon names. */
export const MAP_NODE_ICONS = [
  { value: "map-pin", label: "Pin" },
  { value: "castle", label: "Castle" },
  { value: "swords", label: "Swords" },
  { value: "tent", label: "Camp" },
  { value: "trees", label: "Forest" },
  { value: "mountain", label: "Mountain" },
  { value: "anchor", label: "Port" },
  { value: "church", label: "Temple" },
  { value: "store", label: "Shop" },
  { value: "home", label: "Settlement" },
  { value: "crown", label: "Capital" },
  { value: "skull", label: "Danger" },
  { value: "gem", label: "Treasure" },
  { value: "flag", label: "Flag" },
  { value: "shield", label: "Fortress" },
  { value: "warehouse", label: "Warehouse" },
  { value: "flame", label: "Fire" },
  { value: "snowflake", label: "Frozen" },
  { value: "compass", label: "Compass" },
  { value: "star", label: "Star" },
  { value: "circle-dot", label: "Point" },
  { value: "triangle", label: "Marker" },
] as const;

/**
 * Canonical icon-name → lucide import-name map.
 * Used by MapCanvas, MapGeoCanvas, and any renderer that shows node icons.
 * Import lucide components lazily at the component level; this map just stores the names.
 */
export const MAP_ICON_NAMES = MAP_NODE_ICONS.map(i => i.value);

/** Preset colors for node display */
export const MAP_NODE_COLORS = [
  { value: "hsl(var(--foreground))", label: "Default" },
  { value: "hsl(var(--primary))", label: "Primary" },
  { value: "hsl(var(--destructive))", label: "Red" },
  { value: "hsl(0 0% 100%)", label: "White" },
  { value: "hsl(210 80% 55%)", label: "Blue" },
  { value: "hsl(140 60% 45%)", label: "Green" },
  { value: "hsl(45 90% 55%)", label: "Gold" },
  { value: "hsl(280 60% 55%)", label: "Purple" },
  { value: "hsl(25 90% 55%)", label: "Orange" },
  { value: "hsl(0 0% 50%)", label: "Gray" },
] as const;
